import type { EnvironmentThreadShell } from "@cleo/client-runtime/state/shell";
import { canSettle } from "@cleo/client-runtime/state/thread-settled";
import {
  pinOrderKeyBetween,
  generateSpreadPinOrderKeys,
} from "@cleo/client-runtime/state/thread-sort";
import type { RootSessionStatus } from "~/rootSessionStrip";
import type { RoomRailGroups, RoomRailSessionNode } from "~/neucleos/projectRoomRailGroups";
import {
  moveRoomRailItem,
  type RoomRailFilter,
  type RoomRailPreferences,
} from "~/neucleos/roomRailArrangement";
import { ROOT_SESSION_STATUS_LABELS } from "~/rootSessionStrip";

/**
 * UP13 (GOALS/GOALS-UPSTREAM-PARITY.html#UP13) — the Room Rail's pure laws,
 * kept apart from the DOM so each behavior has a story that never mounts:
 * the settle/restore and stop shortcuts, the file-drop plan, the search row,
 * the compact row's metrics, the persisted active order and the cross-section
 * drag destination. Behaviors re-created behind Cleo's names; the upstream
 * pull requests are references, never acceptance.
 */

// ── Shortcuts (upstream #8089, #4308) ─────────────────────────────────────

export type RoomRailShortcutAction =
  | { readonly kind: "settle" }
  | { readonly kind: "restore" }
  | { readonly kind: "stop" };

/**
 * One shortcut settles the addressed session's thread or restores a settled
 * one; another stops its running turn. Both refuse while the terminal owns
 * the keys, whatever the binding's `when` says, and both refuse without a
 * bound thread (an unbound session row has nothing to settle or stop).
 */
export function resolveRoomRailShortcutAction(
  command: string | null,
  input: {
    readonly shell: EnvironmentThreadShell | null | undefined;
    readonly terminalFocus: boolean;
    readonly now: string;
  },
): RoomRailShortcutAction | null {
  if (command !== "thread.settle" && command !== "thread.stop") return null;
  if (input.terminalFocus || !input.shell) return null;
  if (command === "thread.stop") {
    const status = input.shell.session?.status;
    return status === "running" || status === "starting" ? { kind: "stop" } : null;
  }
  if (input.shell.settledOverride === "settled") return { kind: "restore" };
  return canSettle(input.shell, { now: input.now }) ? { kind: "settle" } : null;
}

// ── File drops onto rows (upstream #7892) ─────────────────────────────────

export function dragCarriesFiles(dataTransfer: Pick<DataTransfer, "types"> | null): boolean {
  return dataTransfer !== null && Array.from(dataTransfer.types).includes("Files");
}

/**
 * The composer's own attachment rules, applied before the session opens:
 * image files only, the wire cap on the count. Nothing here sends.
 */
export function planDroppedFiles(
  files: ReadonlyArray<Pick<File, "name" | "type">>,
  input: { readonly existingCount: number; readonly maxAttachments: number },
): { readonly accepted: number[]; readonly rejected: string | null } {
  const accepted: number[] = [];
  let rejected: string | null = null;
  let reserved = input.existingCount;
  files.forEach((file, index) => {
    if (!file.type.startsWith("image/")) {
      rejected = `Unsupported file type for '${file.name}'. Please attach image files only.`;
      return;
    }
    if (reserved >= input.maxAttachments) {
      rejected = `You can attach up to ${input.maxAttachments} images per message.`;
      return;
    }
    accepted.push(index);
    reserved += 1;
  });
  return { accepted, rejected };
}

// ── Search row with the scope folded in (upstream #11315) ─────────────────

export function normalizeRoomRailQuery(query: string): string {
  return query.trim().toLocaleLowerCase();
}

/** Keeps a session when its label matches or any descendant does; rooms keep only matching sessions. */
export function searchRoomRailGroups(groups: RoomRailGroups, query: string): RoomRailGroups {
  const needle = normalizeRoomRailQuery(query);
  if (needle.length === 0) return groups;
  const nodes = (entries: readonly RoomRailSessionNode[]): RoomRailSessionNode[] =>
    entries.flatMap((node) => {
      const children = nodes(node.children);
      return node.session.label.toLocaleLowerCase().includes(needle) || children.length > 0
        ? [{ ...node, children }]
        : [];
    });
  return {
    ...groups,
    rooms: groups.rooms
      .map((entry) => ({ ...entry, sessions: nodes(entry.sessions) }))
      .filter((entry) => entry.sessions.length > 0),
  };
}

export const ROOM_RAIL_SCOPES: Record<RoomRailFilter, string> = {
  all: "All open sessions",
  needs: "Needs you",
  working: "Working",
};
/** The scope IS the arrangement's filter; one name in the search row, one in the Arrange menu. */
export type RoomRailScope = RoomRailFilter;

// ── Compact rows (upstream #9417, #11525) ─────────────────────────────────

/** The compact row: 36px carrying status, PR badge and time under the label. */
export const ROOM_RAIL_COMPACT_ROW_PX = 36;

export interface RoomRailCompactMetrics {
  readonly heightPx: number;
  readonly status: string | null;
  readonly changeRequest: "open" | "closed" | "merged" | null;
  readonly time: string | null;
}

export function resolveRoomRailCompactMetrics(input: {
  readonly status: RootSessionStatus | null;
  readonly changeRequest: "open" | "closed" | "merged" | null | undefined;
  readonly lastActivityMs: number | null | undefined;
  readonly nowMs: number;
}): RoomRailCompactMetrics {
  return {
    heightPx: ROOM_RAIL_COMPACT_ROW_PX,
    status: input.status === null ? null : ROOT_SESSION_STATUS_LABELS[input.status],
    changeRequest: input.changeRequest ?? null,
    time:
      input.lastActivityMs && input.lastActivityMs > 0
        ? formatRelativeRailTime(input.nowMs - input.lastActivityMs)
        : null,
  };
}

/** Short relative labels that fit a 36px row's second line. */
export function formatRelativeRailTime(elapsedMs: number): string {
  const seconds = Math.max(0, Math.floor(elapsedMs / 1000));
  if (seconds < 60) return "now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks}w`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;
  return `${Math.floor(days / 365)}y`;
}

// ── Persisted active order (upstream #9729) ───────────────────────────────

/**
 * Manual placement written through `thread.active.reorder`: one fractional
 * key on the moved thread when both neighbors carry keys (or are absent);
 * a one-time spread over the run when a neighbor is keyless. Same key
 * math as the pinned block so Desk and Pocket converge on one order.
 */
export function planActiveReorder(input: {
  readonly orderedIds: readonly string[];
  readonly keysById: ReadonlyMap<string, string | null | undefined>;
  readonly movedId: string;
}): ReadonlyArray<{ readonly id: string; readonly orderKey: string }> {
  const { orderedIds, keysById, movedId } = input;
  const movedIndex = orderedIds.indexOf(movedId);
  if (movedIndex === -1) return [];
  const beforeId = movedIndex > 0 ? orderedIds[movedIndex - 1] : null;
  const afterId = movedIndex < orderedIds.length - 1 ? orderedIds[movedIndex + 1] : null;
  const beforeKey = beforeId != null ? (keysById.get(beforeId) ?? null) : null;
  const afterKey = afterId != null ? (keysById.get(afterId) ?? null) : null;
  if ((beforeId === null || beforeKey != null) && (afterId === null || afterKey != null)) {
    const key = pinOrderKeyBetween(beforeKey, afterKey);
    if (key !== null) return [{ id: movedId, orderKey: key }];
  }
  const keys = generateSpreadPinOrderKeys(orderedIds.length);
  return orderedIds.flatMap((id, index) => {
    const key = keys[index]!;
    return keysById.get(id) === key ? [] : [{ id, orderKey: key }];
  });
}

/**
 * The sibling order the rail shows: threads carrying a persisted active key
 * first (string order), then the local manual order for the rest — so a
 * refresh, or Pocket, reads the same arrangement the World holds.
 */
export function mergeActiveOrder(
  localOrder: readonly string[],
  siblings: readonly string[],
  keyOf: (sessionId: string) => string | null | undefined,
): string[] {
  const keyed = siblings
    .filter((id) => keyOf(id) != null)
    .sort((left, right) => {
      const a = keyOf(left)!;
      const b = keyOf(right)!;
      return a < b ? -1 : a > b ? 1 : left.localeCompare(right);
    });
  if (keyed.length === 0) return [...localOrder];
  const keyedSet = new Set(keyed);
  const rest = [
    ...localOrder.filter((id) => !keyedSet.has(id) && siblings.includes(id)),
    ...siblings.filter((id) => !keyedSet.has(id) && !localOrder.includes(id)),
  ];
  return [...keyed, ...rest];
}

// ── Drag across sections with destination cues (upstream #9731, #9750) ────

export type RoomRailDragSection = "pinned" | "room";

export type RoomRailDragDestination =
  | { readonly kind: "reorder"; readonly section: RoomRailDragSection }
  | { readonly kind: "pin"; readonly label: "→ Pinned" }
  | { readonly kind: "unpin"; readonly label: "→ Room" };

/** What a drop would do, and the badge the dragged row wears while crossing. */
export function resolveRoomRailDragDestination(
  source: { readonly kind: string },
  over: { readonly kind: string } | null,
): RoomRailDragDestination | null {
  if (over === null) return null;
  if (source.kind === over.kind) {
    if (source.kind === "pin") return { kind: "reorder", section: "pinned" };
    if (source.kind === "session") return { kind: "reorder", section: "room" };
    return null;
  }
  if (source.kind === "session" && over.kind === "pin") return { kind: "pin", label: "→ Pinned" };
  if (source.kind === "pin" && over.kind === "session") return { kind: "unpin", label: "→ Room" };
  return null;
}

/** What a drag needs to know about either end: the row's kind, id and list. */
export interface RoomRailDropEnd {
  readonly kind: "room" | "session" | "pin" | "launcher" | "context-view";
  readonly id: string;
  readonly parentId: string;
}

/**
 * The one drop law for the rail: a drop within a section reorders it (same
 * list only — a session never crosses rooms by drag); a session dropped on
 * the pinned block pins at that slot; a pin dropped among a room's sessions
 * unpins. Launchers and context views keep their own doors. Returns the
 * next arrangement with the toast line and the destination the cue showed.
 */
export function applyRoomRailDrop(
  prefs: RoomRailPreferences,
  groups: RoomRailGroups,
  source: RoomRailDropEnd,
  over: RoomRailDropEnd,
  after: boolean,
): {
  readonly next: RoomRailPreferences;
  readonly destination: RoomRailDragDestination;
  readonly announcement: string;
} | null {
  if (source.kind === "launcher" || source.kind === "context-view") return null;
  const destination = resolveRoomRailDragDestination(source, over);
  if (destination === null) return null;
  if (destination.kind === "reorder") {
    if (source.parentId !== over.parentId) return null;
    const next = moveRoomRailItem(prefs, groups, source.kind, source.id, over.id, after);
    return next === null
      ? null
      : {
          next,
          destination,
          announcement:
            source.kind === "pin"
              ? "Pinned session order updated"
              : `${source.kind === "room" ? "Room" : "Session"} order updated · Manual`,
        };
  }
  const pinned = movePinAcrossSections(
    prefs.pinnedSessionIds,
    destination,
    source.id,
    over.id,
    after,
  );
  return pinned === null
    ? null
    : {
        next: { ...prefs, pinnedSessionIds: pinned },
        destination,
        announcement: destination.kind === "pin" ? "Session pinned" : "Session unpinned",
      };
}

/** Pins at the drop slot, or unpins; a pin never moves the session's room order. */
export function movePinAcrossSections(
  pinnedSessionIds: readonly string[],
  destination: RoomRailDragDestination,
  sessionId: string,
  overId: string,
  after: boolean,
): string[] | null {
  if (destination.kind === "pin") {
    if (pinnedSessionIds.includes(sessionId)) return null;
    const next = [...pinnedSessionIds];
    const at = next.indexOf(overId);
    next.splice(at === -1 ? next.length : at + (after ? 1 : 0), 0, sessionId);
    return next;
  }
  if (destination.kind === "unpin") {
    if (!pinnedSessionIds.includes(sessionId)) return null;
    return pinnedSessionIds.filter((id) => id !== sessionId);
  }
  return null;
}

// ── Minimap turn steps (upstream #8531) ───────────────────────────────────

/** The step law lives beside the controls (chat/TimelineMinimapControls.logic); re-exported so the rail's stories keep one door. */
export { resolveMinimapStepTarget } from "./chat/TimelineMinimapControls.logic";
